class JLFBoutiquePage extends HTMLElement {
  connectedCallback() {
    this.areaTabs = Array.from(this.querySelectorAll('[data-jlf-boutique-area-tab]'));
    this.boutiqueTabs = Array.from(this.querySelectorAll('[data-jlf-boutique-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-jlf-boutique-panel]'));
    this.videos = Array.from(this.querySelectorAll('.jlf-boutique-page__video'));
    this.setupFaqs();
    this.setupVideos();

    this.areaTabs.forEach((tab) => {
      tab.addEventListener('click', this.handleAreaTabClick);
    });

    this.boutiqueTabs.forEach((tab) => {
      tab.addEventListener('click', this.handleBoutiqueTabClick);
    });

    const activeArea = this.areaTabs.find((tab) => tab.classList.contains('is-active')) || this.areaTabs[0];
    if (!this.activateFromHash() && activeArea) {
      this.setActiveArea(activeArea.dataset.areaKey, false);
    }

    window.addEventListener('hashchange', this.handleHashChange);
  }

  disconnectedCallback() {
    this.areaTabs?.forEach((tab) => {
      tab.removeEventListener('click', this.handleAreaTabClick);
    });

    this.boutiqueTabs?.forEach((tab) => {
      tab.removeEventListener('click', this.handleBoutiqueTabClick);
    });

    window.removeEventListener('hashchange', this.handleHashChange);

    this.videos?.forEach((video) => {
      video.removeEventListener('playing', this.handleVideoStateChange);
      video.removeEventListener('pause', this.handleVideoStateChange);
      video.removeEventListener('ended', this.handleVideoStateChange);
    });

    this.querySelectorAll('[data-jlf-boutique-video-play]').forEach((button) => {
      button.removeEventListener('click', this.handleVideoPlayClick);
    });
  }

  handleAreaTabClick = (event) => {
    const areaKey = event.currentTarget.dataset.areaKey;
    this.setActiveArea(areaKey);
  }

  handleBoutiqueTabClick = (event) => {
    const boutiqueId = event.currentTarget.dataset.boutiqueId;
    this.setActiveBoutique(boutiqueId, true);
  };

  handleHashChange = () => {
    this.activateFromHash();
  };

  handleVideoPlayClick = (event) => {
    const media = event.currentTarget.closest('.jlf-boutique-page__hero-media');
    const video = media?.querySelector('.jlf-boutique-page__video');
    video?.play();
  };

  handleVideoStateChange = (event) => {
    this.updateVideoControl(event.currentTarget);
  };

  updateVideoControl(video) {
    const button = video.closest('.jlf-boutique-page__hero-media')?.querySelector('[data-jlf-boutique-video-play]');

    if (button) {
      button.hidden = !video.paused && !video.ended;
    }
  }

  setupVideos() {
    this.videos.forEach((video) => {
      video.addEventListener('playing', this.handleVideoStateChange);
      video.addEventListener('pause', this.handleVideoStateChange);
      video.addEventListener('ended', this.handleVideoStateChange);
      this.updateVideoControl(video);
    });

    this.querySelectorAll('[data-jlf-boutique-video-play]').forEach((button) => {
      button.addEventListener('click', this.handleVideoPlayClick);
    });
  }

  activateFromHash() {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return false;

    const panel = this.panels.find((item) => item.dataset.boutiqueHash === hash || item.id === hash);
    if (!panel) return false;

    this.setActiveArea(panel.dataset.areaKey, false);
    this.setActiveBoutique(panel.dataset.boutiqueId, false);
    return true;
  };

  setupFaqs() {
    const sources = Array.from(this.querySelectorAll('[data-jlf-boutique-faq-source]'));
    const caretIcon = `
      <span class="svg-wrapper icon-caret icon-animated" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </span>
    `;
    const escapeHtml = (value) => value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    sources.forEach((source) => {
      if (source.dataset.jlfFaqReady === 'true') return;

      const lists = Array.from(source.querySelectorAll('ul'));
      if (!lists.length) return;

      source.dataset.jlfFaqReady = 'true';

      lists.forEach((list) => {
        const items = Array.from(list.children).filter((item) => item.matches('li'));
        if (items.length < 2) return;

        const faq = document.createElement('div');
        faq.className = 'jlf-dynamic-faq accordion accordion--caret accordion--dividers';

        for (let index = 0; index < items.length - 1; index += 2) {
          const title = escapeHtml(items[index].textContent.trim());
          const contentHtml = items[index + 1].innerHTML.trim();

          if (!title || !contentHtml) continue;

          const row = document.createElement('accordion-custom');
          row.innerHTML = `
            <details class="details" data-testid="accordion-details">
              <summary class="details__header">
                <span class="jlf-dynamic-faq__title">${title}</span>
                ${caretIcon}
              </summary>
              <div class="details-content">
                <div class="text-block">${contentHtml}</div>
              </div>
            </details>
          `;

          faq.appendChild(row);
        }

        if (faq.children.length) {
          list.replaceWith(faq);
        }
      });
    });
  }

  setActiveArea(areaKey, activateFirstBoutique = true) {
    this.areaTabs.forEach((tab) => {
      const isActive = tab.dataset.areaKey === areaKey;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    const areaBoutiqueTabs = this.boutiqueTabs.filter((tab) => tab.dataset.areaKey === areaKey);

    this.boutiqueTabs.forEach((tab) => {
      const belongsToArea = tab.dataset.areaKey === areaKey;
      tab.hidden = !belongsToArea;
      if (!belongsToArea) {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
      }
    });

    const currentVisibleTab = areaBoutiqueTabs.find((tab) => tab.classList.contains('is-active'));
    const nextTab = activateFirstBoutique ? areaBoutiqueTabs[0] : currentVisibleTab || areaBoutiqueTabs[0];

    if (nextTab) {
      this.setActiveBoutique(nextTab.dataset.boutiqueId, activateFirstBoutique);
    }
  }

  setActiveBoutique(boutiqueId, updateHash = false) {
    this.boutiqueTabs.forEach((tab) => {
      const isActive = tab.dataset.boutiqueId === boutiqueId && !tab.hidden;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.boutiqueId === boutiqueId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);

      if (!isActive) {
        const video = panel.querySelector('.jlf-boutique-page__video');
        if (video && !video.paused) video.pause();
      } else {
        const video = panel.querySelector('.jlf-boutique-page__video');
        if (video?.autoplay) {
          video.play().catch(() => {
            this.updateVideoControl(video);
          });
        }
      }
    });

    if (updateHash) {
      const panel = this.panels.find((item) => item.dataset.boutiqueId === boutiqueId);
      if (panel?.dataset.boutiqueHash) {
        history.pushState(null, '', `#${panel.dataset.boutiqueHash}`);
      }
    }
  }
}

if (!customElements.get('jlf-boutique-page')) {
  customElements.define('jlf-boutique-page', JLFBoutiquePage);
}
